import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  Badge,
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Card,
  CardContent,
  Collapse,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  QuestionAnswer as QuestionAnswerIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from '../config/axios';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/config';

const FALLBACK_POLLING_INTERVAL = 300000; // 5 minutes - only as fallback if SSE fails

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: 10030, // Higher than all floating icons (which use 9998-10020)
  '& .MuiDrawer-paper': {
    width: 400,
    zIndex: 10030, // Ensure the paper also has high z-index
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  '& .MuiBackdrop-root': {
    zIndex: 10029, // Backdrop should be slightly lower than drawer
  },
}));

const QuestionCard = styled(Card)(({ theme, isUnread }) => ({
  marginBottom: theme.spacing(2),
  border: isUnread ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  backgroundColor: isUnread ? theme.palette.action.hover : 'inherit',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const NotificationBell = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [error, setError] = useState(null);
  const [sseConnected, setSseConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get('/community/questions/unread-count');
      if (response.data.success) {
        setUnreadCount(response.data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
      // Don't show error for polling, just log it
    }
  };

  // Fetch unread answered questions
  const fetchUnreadQuestions = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get('/community/questions/unread-answered', {
        params: { limit: 50 }
      });
      
      if (response.data.success) {
        setQuestions(response.data.questions || []);
        setUnreadCount(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread questions:', error);
      setError('Fehler beim Laden der Fragen');
    } finally {
      setLoading(false);
    }
  };

  // Mark a question as read
  const markAsRead = async (questionId) => {
    try {
      // Update local state immediately to mark as read (optimistic update)
      setQuestions(prev => prev.map(q => 
        q.id === questionId 
          ? { ...q, read_at: new Date().toISOString() }
          : q
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Call API in background
      const response = await axios.post(`/community/questions/mark-read/${questionId}`);
      
      if (!response.data.success) {
        // Revert on failure
        setQuestions(prev => prev.map(q => 
          q.id === questionId 
            ? { ...q, read_at: null }
            : q
        ));
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error marking question as read:', error);
      // Revert on error
      setQuestions(prev => prev.map(q => 
        q.id === questionId 
          ? { ...q, read_at: null }
          : q
      ));
      setUnreadCount(prev => prev + 1);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const response = await axios.post('/community/questions/mark-all-read');
      
      if (response.data.success) {
        setQuestions([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Toggle question expansion
  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
        // Mark as read when expanded (only if not already read)
        const question = questions.find(q => q.id === questionId);
        if (question && !question.read_at) {
          markAsRead(questionId);
        }
      }
      return newSet;
    });
  };

  // Open drawer and fetch questions
  const handleDrawerOpen = () => {
    setDrawerOpen(true);
    fetchUnreadQuestions();
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Minuten`;
    if (diffHours < 24) return `vor ${diffHours} Stunde${diffHours > 1 ? 'n' : ''}`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Set up SSE connection for real-time notifications
  useEffect(() => {
    if (!user) return;

    // Get token for SSE connection
    const token = localStorage.getItem('token');
    if (!token) return;

    // Build SSE URL with token as query parameter (EventSource doesn't support custom headers)
    const sseUrl = `${API_CONFIG.BASE_URL}/notifications/stream?token=${encodeURIComponent(token)}`;
    
    // Create EventSource connection
    const eventSource = new EventSource(sseUrl, {
      withCredentials: true
    });

    eventSourceRef.current = eventSource;

    // Handle connection open
    eventSource.onopen = () => {
      console.log('SSE connection established');
      setSseConnected(true);
      // Fetch initial count when connected
      fetchUnreadCount();
    };

    // Handle messages
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'connected') {
          console.log('SSE connected:', data.message);
          return;
        }
        
        if (data.type === 'question_answered') {
          console.log('Question answered notification received:', data.data);
          // Refresh unread count immediately
          fetchUnreadCount();
          // If drawer is open, refresh the questions list
          if (drawerOpen) {
            fetchUnreadQuestions();
          }
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    // Handle errors
    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      setSseConnected(false);
      // Fallback to polling if SSE fails
      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(() => {
          fetchUnreadCount();
        }, FALLBACK_POLLING_INTERVAL);
      }
    };

    // Initial fetch
    fetchUnreadCount();

    // Cleanup
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [user]);

  // Refresh count when drawer opens/closes
  useEffect(() => {
    if (drawerOpen) {
      fetchUnreadQuestions();
    } else {
      fetchUnreadCount();
    }
  }, [drawerOpen, user]);

  if (!user) {
    return null;
  }

  return (
    <>
      <Tooltip title="Benachrichtigungen">
        <IconButton
          color="inherit"
          onClick={handleDrawerOpen}
          sx={{ 
            color: 'inherit',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <StyledDrawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          style: { zIndex: 10030 },
        }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuestionAnswerIcon color="primary" />
              <Typography variant="h6" component="div">
                Beantwortete Fragen
              </Typography>
              {unreadCount > 0 && (
                <Chip
                  label={unreadCount}
                  color="primary"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
            <IconButton onClick={handleDrawerClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Actions */}
          {questions.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={markAllAsRead}
                sx={{ flex: 1 }}
              >
                Alle als gelesen markieren
              </Button>
            </Box>
          )}

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : questions.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Keine ungelesenen Antworten
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Alle deine Fragen wurden bereits gelesen.
                </Typography>
              </Box>
            ) : (
              <List>
                {questions.map((question, index) => {
                  const isExpanded = expandedQuestions.has(question.id);
                  const isUnread = !question.read_at;

                  return (
                    <React.Fragment key={question.id}>
                      <QuestionCard isUnread={isUnread}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: isUnread ? 600 : 400, mb: 1 }}>
                                {question.question}
                              </Typography>
                              {question.kategorie && (
                                <Chip
                                  label={question.kategorie}
                                  size="small"
                                  sx={{ mb: 1, mr: 1 }}
                                />
                              )}
                              {question.answered_at && (
                                <Typography variant="caption" color="text.secondary">
                                  Beantwortet {formatDate(question.answered_at)}
                                </Typography>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => toggleQuestion(question.id)}
                              sx={{ ml: 1 }}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>

                          <Collapse in={isExpanded}>
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                                Antwort:
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                {question.answer}
                              </Typography>
                            </Box>
                          </Collapse>
                        </CardContent>
                      </QuestionCard>
                      {index < questions.length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>
      </StyledDrawer>
    </>
  );
};

export default NotificationBell;

